/**
 * Contenido (copy) del catálogo de planes del paywall — rediseño de Figma
 * "Pricing" (node 2044:448). Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
 *
 * Es SÓLO presentación: precios, países y descuentos siguen saliendo de la
 * lógica que ya vivía en `Paywall.tsx` (`getPlanPrice` / `formatPrice` /
 * cupón Hot Sale). Acá vive únicamente lo que el diseño muestra como texto.
 *
 * ⚠️ `credits` es un número **de copy**, autoría del diseño: el backend no tiene
 * hoy una noción de "créditos por plan" (la cuota real es `max_fotos_mes`, y
 * `utils/credits.ts` sólo define el costo por acción del editor, donde
 * 1 imagen = 1 crédito). Los conteos de `generates` (imágenes/videos) SÍ son los
 * reales del catálogo (`max_fotos_mes` y los videos por tier). Si el equipo
 * define una cuota de créditos de verdad, este campo debe pasar a derivarse
 * de ella.
 */

/** Card del selector: los 3 planes que muestra el diseño. */
export type PricingPlanKey = 'emprendimiento' | 'negocio' | 'enterprise';

/** Orden de izquierda a derecha en el selector (Básico · Pro · Ultra). */
export const PRICING_PLAN_KEYS: PricingPlanKey[] = [
  'emprendimiento',
  'negocio',
  'enterprise',
];

/** Plan destacado con el badge "Popular" sobre el selector. */
export const RECOMMENDED_PLAN: PricingPlanKey = 'negocio';

export interface PricingPlanContent {
  /** Etiqueta corta del selector segmentado. */
  tab: string;
  /** Título dentro de la card. */
  title: string;
  /** Bajada bajo el título. */
  subtitle: string;
  /**
   * Créditos mensuales que muestra la caja destacada. Ver la advertencia del
   * encabezado: es copy del diseño, no un dato del backend.
   */
  credits: number;
  /** Líneas de "Se pueden generar:" (conteos reales del catálogo). */
  generates: string[];
  /**
   * Línea de herencia ("Todo lo del plan X y..."), con ícono de flecha y en
   * gris. `null` en Básico, que no hereda de nadie.
   */
  inherits: string | null;
  /** Features con check. */
  features: string[];
  /**
   * Lo que el plan NO trae, con cruz y en gris, debajo de las features. Va
   * explícito porque es lo que más se pregunta al elegir (hoy: que Básico no
   * genera videos). Opcional: sólo lo lleva el plan que tiene algo que aclarar.
   */
  excluded?: string[];
  /**
   * Beneficio destacado en una caja propia, entre los créditos y las features
   * (hoy: la capacitación personalizada de Ultra). Es un servicio, no una
   * capacidad de la app, y en la lista de checks se perdía.
   */
  highlight?: { title: string; description: string };
  /** Label del CTA fijo del pie. */
  cta: string;
}

/**
 * Créditos por segundo de video, la tarifa con la que se comunican los planes. Los
 * planes hablan de SEGUNDOS de video y no de videos: un "video" no tiene largo fijo,
 * así que cualquier conteo de videos dependía de un largo supuesto. Los segundos son
 * la unidad que el usuario elige al generar. La línea sigue diciendo **"hasta"**
 * porque es el techo si toda la bolsa va a video (es alternativa a las imágenes).
 * Ver ADR-2026-09-24-planes-en-segundos-de-video (supersede
 * ADR-2026-08-03-videos-por-plan-se-calculan-con-el-piso-real).
 */
export const CREDITOS_POR_SEGUNDO_DE_VIDEO = 2;

/** Cuántos segundos de video entran en `creditos`, si toda la bolsa va a video. */
export const segundosDeVideoPorCreditos = (creditos: number): number =>
  Math.floor(creditos / CREDITOS_POR_SEGUNDO_DE_VIDEO);

/** Línea de imágenes de "Se pueden generar:". Es exacta: 1 crédito = 1 imagen. */
export const lineaImagenes = (creditos: number): string =>
  `${creditos.toLocaleString('es-AR')} imágenes`;

/** Línea de video de "Se pueden generar:". Es un TECHO, de ahí el "hasta". */
export const lineaVideos = (creditos: number): string =>
  `hasta ${segundosDeVideoPorCreditos(creditos).toLocaleString('es-AR')} segundos de video`;

/**
 * Cuota mensual de créditos por plan (Ultra usa `ULTRA_TIER_CREDITS`, que depende
 * del tier). Existe como constante para que `credits` y las líneas de "Se pueden
 * generar:" salgan del MISMO número: cuando estaban sueltos, la cuota decía 250 y
 * los videos seguían calculados sobre otra cifra (ADR-2026-08-03-videos-por-plan-se-calculan-con-el-piso-real).
 */
const CREDITOS_PLAN = { emprendimiento: 60, negocio: 250, enterprise: 500 } as const;

export const PRICING_CONTENT: Record<PricingPlanKey, PricingPlanContent> = {
  emprendimiento: {
    tab: 'Básico',
    title: 'Básico',
    subtitle: 'Para emprendimientos que publican pocos productos por mes',
    credits: CREDITOS_PLAN.emprendimiento,
    // Sin línea de videos a propósito: la generación de videos arranca en Pro (es una
    // feature del plan, no una cuestión de saldo).
    generates: [lineaImagenes(CREDITOS_PLAN.emprendimiento)],
    inherits: null,
    // El copy de features, bajadas y beneficios es el de la landing de precios:
    // la app y la web tienen que prometer lo mismo
    // (ADR-2026-10-14-planes-de-la-app-con-el-copy-de-la-landing).
    features: [
      'Todo tipo de imágenes de producto: fondo blanco, catálogo, con modelo y ambientadas',
      'Descargas en calidad Full HD (1080p), ideal para redes sociales',
      'Soporte por correo electrónico',
    ],
    excluded: ['No incluye generación de videos'],
    cta: 'Suscribirme al Básico',
  },
  negocio: {
    tab: 'Pro',
    title: 'Pro',
    subtitle: 'Para negocios con publicación frecuente que requieren imágenes y videos',
    // 250 = `company_profiles.max_fotos_mes` real del plan `negocio` (101 empresas
    // activas con ese valor). Mostraba 300, que no lo otorga nadie
    // (ADR-2026-08-03-creditos-por-plan-mienten).
    credits: CREDITOS_PLAN.negocio,
    generates: [
      lineaImagenes(CREDITOS_PLAN.negocio),
      lineaVideos(CREDITOS_PLAN.negocio),
    ],
    inherits: 'Todo lo incluido en Básico, más:',
    features: [
      'Videos de producto y videos con avatares que presentan tu producto, listos para subir a tu Mercado Libre, Tiendanube o redes sociales',
      'Edición avanzada de video',
      'Descargas en máxima calidad (2K y 4K), ideal para tiendas online y banners',
      'Soporte por WhatsApp',
    ],
    cta: 'Suscribirme al Pro',
  },
  enterprise: {
    tab: 'Ultra',
    title: 'Ultra',
    subtitle: 'Para catálogos extensos, hasta 3 marcas y alto volumen de producción',
    // Ultra es multi-tier: `credits` y `generates` de acá son el fallback del
    // primer tier; la card los pisa con los del tier elegido en el slider
    // (`ULTRA_TIER_CREDITS`).
    credits: CREDITOS_PLAN.enterprise,
    generates: [
      lineaImagenes(CREDITOS_PLAN.enterprise),
      lineaVideos(CREDITOS_PLAN.enterprise),
    ],
    highlight: {
      title: 'Capacitación personalizada',
      description: 'Te capacitamos para que uses Atlas de la forma más óptima',
    },
    inherits: 'Todo lo incluido en Pro, más:',
    features: [
      'Hasta 3 marcas en una misma cuenta: mantené separados los productos, creaciones y configuraciones de cada una',
      'Acceso anticipado a nuevas funciones',
      'Soporte prioritario por WhatsApp',
    ],
    cta: 'Suscribirme al Ultra',
  },
};

/**
 * Créditos por tier de Ultra, en el mismo orden que `ULTRA_TIERS` de `pricingPlans`.
 *
 * **Son exactamente la cuota de imágenes del tier**, porque 1 crédito = 1 imagen
 * entregada (el backend debita por output: ver `expectedDeliveredCount` +
 * `debitDelivered`). Antes decían `[1000, 2000, 4000]` —el doble de lo que otorga
 * `max_fotos_mes` en cada plan (`enterprise1` 500, `enterprise2` 1000)— porque se
 * asumió que crédito e imagen eran unidades distintas.
 * Ver ADR-2026-08-03-creditos-por-plan-mienten.
 */
export const ULTRA_TIER_CREDITS = [500, 1000, 2000];

// Acá vivía `CREDITOS_BIENVENIDA`: los créditos EXTRA del primer mes que el
// servidor de pagos acreditaba al darse de alta desde gratis (`activationCredits`,
// +30 Básico / +50 Pro / +100 Ultra). El beneficio se discontinuó y se saca de los
// dos lados —front y servidor de pagos— así que la tabla no queda "por las dudas":
// una constante sin uso es una promesa esperando a que alguien la vuelva a pintar.
// Ver ADR-2026-08-18-fuera-creditos-extra-de-bienvenida.

/** Etiqueta corta de cada tope del slider de Ultra (imágenes por mes). */
export const ULTRA_TIER_LABELS = ['500', '1000', '2000'];

/**
 * Beneficios comunes a los tres planes, en una fila sobre las cards de la
 * comparativa (los mismos de la landing). `soloArgentina`: la factura A o B en
 * pesos sólo existe para cuentas argentinas; en otro país la línea no va.
 */
export const BENEFICIOS_DE_TODOS_LOS_PLANES: { texto: string; soloArgentina?: boolean }[] = [
  { texto: 'Factura A o B en pesos', soloArgentina: true },
  { texto: 'Contenido para historias, publicaciones y tienda online' },
  { texto: 'Pausa o cancelación en cualquier momento' },
];

/** Descuento que muestra el badge del toggle Anual (visual, ADR-2026-07-28-switch-mensual-anual-visual-solo). */
export const ANNUAL_DISCOUNT_LABEL = '20% OFF';

/**
 * ⚠️ PLACEHOLDER (ADR-2026-07-28-switch-mensual-anual-visual-solo). No hay plan anual en el backend: este porcentaje sólo
 * existe para poder dibujar la opción "Anual" del selector de facturación que ven
 * las cuentas `dev_tester`. Cuando el plan anual exista de verdad, el precio tiene
 * que venir del catálogo/proveedor, NO calcularse acá.
 */
export const ANNUAL_DISCOUNT_RATE = 0.2;
